import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { BillingSummary } from '@/billing/api/FetchBillingList';
import { useChangeBillingStatus } from '@/billing/hooks/useChangeBillingStatus';
import { useIsLockedByOtherUser } from '@/billing/hooks/useIsLockedByOtherUser';
import { Button } from '@/common/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';

export type ActionButtonsProps = {
  billing: BillingSummary;
  onClose: () => void;
};

const ActionButtons = ({ billing, onClose }: ActionButtonsProps) => {
  const { t } = useTranslation();
  const isLocked = useIsLockedByOtherUser(billing);
  const { changeStatus, isPending } = useChangeBillingStatus();

  const handleError = (error: unknown) => {
    if (
      isAxiosError(error) &&
      error?.response?.data?.error === 'BillingLockedException'
    ) {
      toast.error(t('billing.drawer.assignmentConflictError'));
    } else {
      toast.error(t('billing.drawer.statusChangeError'));
    }
  };

  const handleConfirm = () => {
    changeStatus(
      { billingUuid: billing.uuid, status: 'COMPLETED' },
      {
        onSuccess: () => {
          toast.success(t('billing.drawer.statusChanged'));
          onClose();
        },
        onError: handleError,
      },
    );
  };

  return (
    <div className="shrink-0 p-4 space-y-2 bg-primary-foreground border-t border-sidebar-border">
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Button
              className="w-full"
              disabled={isLocked || isPending}
              onClick={handleConfirm}
            >
              {t('billing.drawer.saveAsCompleted')}
            </Button>
          </div>
        </TooltipTrigger>
        {isLocked && (
          <TooltipContent>
            {t('billing.drawer.openByDifferentUser')}
          </TooltipContent>
        )}
      </Tooltip>
      <Button variant="outline" className="w-full" onClick={onClose}>
        {t('actions.cancel')}
      </Button>
    </div>
  );
};

export default ActionButtons;
